import { openDB, type DBSchema, type IDBPDatabase } from "idb"
import type { Asistente, QueueItem, Category, CertifiedUser, Group } from "../types"
import type { DynamicUser, DynamicSchema } from "../types/dynamic"

interface PanelDB extends DBSchema {
  asistentes: {
    key: string
    value: Asistente
  }
  queue: {
    key: number
    value: QueueItem
    indexes: { timestamp: number }
  }
  categories: {
    key: number
    value: Category
  }
  certifications: {
    key: number
    value: CertifiedUser
  }
  groups: {
    key: string
    value: Group & { key: string }
  }
  // New stores for dynamic system
  dynamicUsers: {
    key: number
    value: DynamicUser
  }
  dynamicSchema: {
    key: string
    value: DynamicSchema & { key: string }
  }
}

let db: IDBPDatabase<PanelDB> | null = null

export const initDB = async (): Promise<IDBPDatabase<PanelDB>> => {
  if (db) return db

  try {
    db = await openDB<PanelDB>("panel-usuarios-app", 7, {
      upgrade(db, oldVersion) {
        // Clear old stores if they exist
        if (oldVersion < 4) {
          if (db.objectStoreNames.contains("asistentes")) {
            db.deleteObjectStore("asistentes")
          }
          if (db.objectStoreNames.contains("queue")) {
            db.deleteObjectStore("queue")
          }
          if (db.objectStoreNames.contains("categories")) {
            db.deleteObjectStore("categories")
          }
          if (db.objectStoreNames.contains("certifications")) {
            db.deleteObjectStore("certifications")
          }
        }

        // Create asistentes store
        if (!db.objectStoreNames.contains("asistentes")) {
          db.createObjectStore("asistentes", { keyPath: "asistente_folio" })
        }

        // Create queue store with auto-increment
        if (!db.objectStoreNames.contains("queue")) {
          db.createObjectStore("queue", { autoIncrement: true })
        }

        // Create categories store
        if (!db.objectStoreNames.contains("categories")) {
          db.createObjectStore("categories", { keyPath: "id" })
        }

        // Create certifications store
        if (!db.objectStoreNames.contains("certifications")) {
          db.createObjectStore("certifications", { keyPath: "id" })
        }

        // Create categories store for version 2
        if (oldVersion < 2 && !db.objectStoreNames.contains("categories")) {
          db.createObjectStore("categories", { keyPath: "id" })
        }

        // Create certifications store for version 4
        if (oldVersion < 4 && !db.objectStoreNames.contains("certifications")) {
          db.createObjectStore("certifications", { keyPath: "id" })
        }

        // Create groups store for version 6
        if (oldVersion < 6 && !db.objectStoreNames.contains("groups")) {
          db.createObjectStore("groups", { keyPath: "key" })
        }

        // Create dynamic system stores for version 7
        if (oldVersion < 7) {
          if (!db.objectStoreNames.contains("dynamicUsers")) {
            db.createObjectStore("dynamicUsers", { keyPath: "id" })
          }
          if (!db.objectStoreNames.contains("dynamicSchema")) {
            db.createObjectStore("dynamicSchema", { keyPath: "key" })
          }
        }
      },
    })

    console.log("💾 IndexedDB inicializada correctamente")
    return db
  } catch (error) {
    console.error("❌ Error inicializando IndexedDB:", error)
    throw error
  }
}

// Clear all data (for debugging)
export const clearAllData = async (): Promise<void> => {
  try {
    const database = await initDB()
    const tx = database.transaction(["asistentes", "queue", "categories", "certifications", "groups", "dynamicUsers", "dynamicSchema"], "readwrite")
    await tx.objectStore("asistentes").clear()
    await tx.objectStore("queue").clear()
    await tx.objectStore("categories").clear()
    await tx.objectStore("certifications").clear()
    await tx.objectStore("groups").clear()
    await tx.objectStore("dynamicUsers").clear()
    await tx.objectStore("dynamicSchema").clear()
    await tx.done
    console.log("🗑️ Todos los datos limpiados")
  } catch (error) {
    console.error("❌ Error limpiando datos:", error)
  }
}

// Helper function to validate asistente data
const validateAsistente = (asistente: Asistente): boolean => {
  return !!(
    asistente &&
    asistente.asistente_folio &&
    typeof asistente.asistente_folio === "string" &&
    asistente.asistente_folio.trim().length > 0 &&
    asistente.asistente_nombre &&
    asistente.asistente_apellido_paterno &&
    asistente.asistente_correo
  )
}

// Helper function to process asistente data before saving
const processAsistenteForStorage = async (asistente: Asistente, preserveLocalApproval: boolean = false): Promise<Asistente> => {
  const processed = { ...asistente }

  // If preserveLocalApproval is true, check existing data to preserve approval status
  if (preserveLocalApproval && processed.asistente_folio) {
    try {
      const database = await initDB()
      const existing = await database.get("asistentes", processed.asistente_folio)

      // Preserve local approval status for category 4 if it was approved locally
      if (existing &&
          existing.tipo_asistente_id === "4" &&
          existing.approved_category_status_congress === "1" &&
          processed.tipo_asistente_id === "4") {
        processed.approved_category_status_congress = "1"
        console.log(`🔒 Preservando estado aprobado local para médico certificado: ${processed.asistente_folio}`)
        return processed
      }
    } catch (error) {
      console.warn("⚠️ Error checking existing approval status:", error)
    }
  }

  // Set approved_category_status_congress based on category and certification
  if (processed.tipo_asistente_id === "4") {
    // For category 4 (Médico certificado), preserve existing value or default to 0
    if (processed.approved_category_status_congress === undefined || processed.approved_category_status_congress === null) {
      processed.approved_category_status_congress = "0"
    }
  } else {
    // For other categories, always set to 0
    processed.approved_category_status_congress = "0"
  }

  return processed
}

// Asistentes operations
export const saveAsistentesToDB = async (asistentes: Asistente[]): Promise<void> => {
  try {
    // First, process all asistentes outside of the transaction
    const processedAsistentes: Asistente[] = []
    let invalidCount = 0

    for (const asistente of asistentes) {
      if (validateAsistente(asistente)) {
        try {
          const processedAsistente = await processAsistenteForStorage(asistente, true)
          processedAsistentes.push(processedAsistente)
        } catch (error) {
          console.warn("⚠️ Error procesando asistente individual:", asistente.asistente_folio, error)
          invalidCount++
        }
      } else {
        console.warn("⚠️ Asistente inválido ignorado:", asistente)
        invalidCount++
      }
    }

    // Now perform database operations in a single transaction
    const database = await initDB()
    const tx = database.transaction("asistentes", "readwrite")

    // Clear existing data
    await tx.store.clear()

    // Add all processed data
    for (const processedAsistente of processedAsistentes) {
      await tx.store.put(processedAsistente)
    }

    await tx.done
    console.log(
      `💾 ${processedAsistentes.length} asistentes guardados en IndexedDB${invalidCount > 0 ? ` (${invalidCount} ignorados)` : ""}`,
    )
  } catch (error) {
    console.error("❌ Error guardando asistentes:", error)
    throw error
  }
}

export const getAsistentesFromDB = async (): Promise<Asistente[]> => {
  try {
    const database = await initDB()
    const asistentes = await database.getAll("asistentes")

    // Filter out any invalid entries
    const validAsistentes = asistentes.filter(validateAsistente)

    console.log(`📱 ${validAsistentes.length} asistentes cargados desde IndexedDB`)
    return validAsistentes
  } catch (error) {
    console.error("❌ Error cargando asistentes:", error)
    return []
  }
}

export const updateAsistenteInDB = async (asistente: Asistente): Promise<void> => {
  try {
    if (!validateAsistente(asistente)) {
      throw new Error("Datos de asistente inválidos")
    }

    const processedAsistente = await processAsistenteForStorage(asistente)
    const database = await initDB()
    await database.put("asistentes", processedAsistente)
    console.log(`💾 Asistente ${asistente.asistente_folio} actualizado en IndexedDB`)
  } catch (error) {
    console.error("❌ Error actualizando asistente:", error)
    throw error
  }
}

// Function to get the maximum asistente_id from existing records
export const getMaxAsistenteId = async (): Promise<number> => {
  try {
    const database = await initDB()
    const allAsistentes = await database.getAll("asistentes")

    let maxId = 0
    for (const existing of allAsistentes) {
      const existingId = parseInt(existing.asistente_id || "0", 10)
      if (existingId > maxId) {
        maxId = existingId
      }
    }

    return maxId
  } catch (error) {
    console.error("❌ Error obteniendo máximo ID:", error)
    return 0
  }
}

export const addAsistenteToDB = async (asistente: Asistente): Promise<void> => {
  try {
    if (!validateAsistente(asistente)) {
      throw new Error("Datos de asistente inválidos")
    }

    const processedAsistente = await processAsistenteForStorage(asistente)

    const database = await initDB()
    await database.add("asistentes", processedAsistente)
    console.log(`💾 Asistente ${asistente.asistente_folio} agregado a IndexedDB con ID ${processedAsistente.asistente_id}`)
  } catch (error) {
    console.error("❌ Error agregando asistente:", error)
    throw error
  }
}

// Queue operations
export const addToQueueDB = async (item: Omit<QueueItem, "id">): Promise<number> => {
  try {
    const database = await initDB()
    const key = await database.add("queue", item as QueueItem)
    console.log(`📝 Elemento agregado a la cola con key ${key}:`, item.type)
    return key as number
  } catch (error) {
    console.error("❌ Error agregando a la cola:", error)
    throw error
  }
}

export const getQueueFromDB = async (): Promise<Array<QueueItem & { id: number }>> => {
  try {
    const database = await initDB()
    const tx = database.transaction("queue", "readonly")
    const store = tx.objectStore("queue")

    const items: Array<QueueItem & { id: number }> = []
    let cursor = await store.openCursor()

    while (cursor) {
      items.push({
        id: cursor.key as number,
        ...cursor.value,
      })
      cursor = await cursor.continue()
    }

    return items
  } catch (error) {
    console.error("❌ Error obteniendo cola:", error)
    return []
  }
}

export const removeFromQueueDB = async (id: number): Promise<void> => {
  try {
    const database = await initDB()
    await database.delete("queue", id)
    console.log(`🗑️ Elemento ${id} eliminado de la cola`)
  } catch (error) {
    console.error("❌ Error eliminando de la cola:", error)
    throw error
  }
}

export const clearQueueDB = async (): Promise<void> => {
  try {
    const database = await initDB()
    await database.clear("queue")
    console.log("🗑️ Cola limpiada completamente")
  } catch (error) {
    console.error("❌ Error limpiando cola:", error)
    throw error
  }
}

// Categories operations
export const saveCategoriesToDB = async (categories: Category[]): Promise<void> => {
  try {
    const database = await initDB()
    const tx = database.transaction("categories", "readwrite")

    // Clear existing data
    await tx.store.clear()

    // Add new data
    for (const category of categories) {
      if (category && category.id && category.description) {
        await tx.store.put(category)
      }
    }

    await tx.done
    console.log(`💾 ${categories.length} categorías guardadas en IndexedDB`)
  } catch (error) {
    console.error("❌ Error guardando categorías:", error)
    throw error
  }
}

export const getCategoriesFromDB = async (): Promise<Category[]> => {
  try {
    const database = await initDB()
    const categories = await database.getAll("categories")
    console.log(`📱 ${categories.length} categorías cargadas desde IndexedDB`)
    return categories
  } catch (error) {
    console.error("❌ Error cargando categorías:", error)
    return []
  }
}

// Certifications operations
export const saveCertificationsToDb = async (certifications: CertifiedUser[]): Promise<void> => {
  try {
    const database = await initDB()
    const tx = database.transaction("certifications", "readwrite")

    // Clear existing data
    await tx.store.clear()

    // Add new data
    for (const certification of certifications) {
      if (certification && certification.id) {
        await tx.store.put(certification)
      }
    }

    await tx.done
    console.log(`💾 ${certifications.length} certificaciones guardadas en IndexedDB`)
  } catch (error) {
    console.error("❌ Error guardando certificaciones:", error)
    throw error
  }
}

export const getCertificationsFromDB = async (): Promise<CertifiedUser[]> => {
  try {
    const database = await initDB()
    const certifications = await database.getAll("certifications")
    console.log(`📱 ${certifications.length} certificaciones cargadas desde IndexedDB`)
    return certifications
  } catch (error) {
    console.error("❌ Error cargando certificaciones:", error)
    return []
  }
}


export const getGroupsFromDB = async (): Promise<Group | null> => {
  try {
    const database = await initDB()
    const groupData = await database.get("groups", "all_groups")

    if (groupData) {
      // Remove the 'key' property and return the rest
      const { key, ...groups } = groupData
      console.log(`📱 ${Object.keys(groups).length} grupos cargados desde IndexedDB`)
      return groups as Group
    }

    console.log(`📱 No hay grupos guardados en IndexedDB`)
    return null
  } catch (error) {
    console.error("❌ Error cargando grupos:", error)
    return null
  }
}

// Dynamic Users operations
export const saveDynamicUsersToDb = async (users: DynamicUser[]): Promise<void> => {
  try {
    const database = await initDB()
    const tx = database.transaction("dynamicUsers", "readwrite")

    // Clear existing data
    await tx.store.clear()

    // Add new data
    for (const user of users) {
      if (user && user.id) {
        await tx.store.put(user)
      }
    }

    await tx.done
    console.log(`💾 ${users.length} usuarios dinámicos guardados en IndexedDB`)
  } catch (error) {
    console.error("❌ Error guardando usuarios dinámicos:", error)
    throw error
  }
}

export const getDynamicUsersFromDB = async (): Promise<DynamicUser[]> => {
  try {
    const database = await initDB()
    const users = await database.getAll("dynamicUsers")
    console.log(`📱 ${users.length} usuarios dinámicos cargados desde IndexedDB`)
    return users
  } catch (error) {
    console.error("❌ Error cargando usuarios dinámicos:", error)
    return []
  }
}

export const updateDynamicUserInDB = async (user: DynamicUser): Promise<void> => {
  try {
    if (!user || !user.id) {
      throw new Error("Datos de usuario dinámico inválidos")
    }

    const database = await initDB()
    await database.put("dynamicUsers", user)
    console.log(`💾 Usuario dinámico ${user.id} actualizado en IndexedDB`)
  } catch (error) {
    console.error("❌ Error actualizando usuario dinámico:", error)
    throw error
  }
}

export const addDynamicUserToDB = async (user: DynamicUser): Promise<void> => {
  try {
    if (!user || !user.id) {
      throw new Error("Datos de usuario dinámico inválidos")
    }

    const database = await initDB()
    // Usar put en lugar de add para permitir actualizaciones
    await database.put("dynamicUsers", user)
    console.log(`💾 Usuario dinámico ${user.id} guardado en IndexedDB`)
  } catch (error) {
    console.error("❌ Error agregando usuario dinámico:", error)
    throw error
  }
}

// Nueva función para merge offline-first de usuarios dinámicos
export const mergeDynamicUsersToDb = async (serverUsers: DynamicUser[]): Promise<void> => {
  try {
    const database = await initDB()

    // Obtener usuarios locales actuales
    const localUsers = await getDynamicUsersFromDB()

    // Crear mapa de usuarios locales por ID
    const localUsersMap = new Map<number, DynamicUser>()
    localUsers.forEach(user => localUsersMap.set(user.id, user))

    console.log(`🔄 Mergeando ${serverUsers.length} usuarios del servidor con ${localUsers.length} usuarios locales`)

    // Hacer merge sin transacción compleja - agregar/actualizar usuarios del servidor
    // que no están en local o que tienen cambios
    for (const serverUser of serverUsers) {
      const localUser = localUsersMap.get(serverUser.id)

      if (!localUser) {
        // Usuario nuevo del servidor - agregarlo
        await database.put("dynamicUsers", serverUser)
        console.log(`📥 Usuario nuevo del servidor agregado: ID ${serverUser.id}`)
      } else {
        // Usuario existe en local - comparar y actualizar solo si hay diferencias significativas
        const serverJson = JSON.stringify(serverUser)
        const localJson = JSON.stringify(localUser)

        if (serverJson !== localJson) {
          // PRIORIZAR DATOS LOCALES - solo actualizar campos específicos del servidor
          // Mantener los datos locales como base y solo actualizar campos no críticos
          const mergedUser = {
            ...serverUser, // Base del servidor
            ...localUser,  // Sobrescribir con datos locales (prioridad local)
            id: serverUser.id // Mantener ID consistente
          }

          await database.put("dynamicUsers", mergedUser)
          console.log(`🔄 Usuario actualizado con prioridad local: ID ${serverUser.id}`)
        }
      }
    }

    console.log(`✅ Merge completado - usuarios locales preservados, ${serverUsers.length} usuarios del servidor procesados`)
  } catch (error) {
    console.error("❌ Error mergeando usuarios dinámicos:", error)
    throw error
  }
}

// Dynamic Schema operations
export const saveDynamicSchemaToDb = async (schema: DynamicSchema): Promise<void> => {
  try {
    const database = await initDB()
    const tx = database.transaction("dynamicSchema", "readwrite")

    // Clear existing data
    await tx.store.clear()

    // Save schema as a single entry with proper typing
    const schemaWithKey = { key: "current_schema", ...schema } as DynamicSchema & { key: string }
    await tx.store.put(schemaWithKey)

    await tx.done
    console.log(`💾 Schema dinámico guardado en IndexedDB con ${Object.keys(schema).length} campos`)
  } catch (error) {
    console.error("❌ Error guardando schema dinámico:", error)
    throw error
  }
}

export const getDynamicSchemaFromDB = async (): Promise<DynamicSchema | null> => {
  try {
    const database = await initDB()
    const schemaData = await database.get("dynamicSchema", "current_schema")

    if (schemaData) {
      // Remove the 'key' property and return the rest
      const { key, ...schema } = schemaData
      console.log(`📱 Schema dinámico cargado desde IndexedDB con ${Object.keys(schema).length} campos`)
      return schema as DynamicSchema
    }

    console.log(`📱 No hay schema dinámico guardado en IndexedDB`)
    return null
  } catch (error) {
    console.error("❌ Error cargando schema dinámico:", error)
    return null
  }
}

// Function to clean temporary duplicate users
export const cleanTemporaryDuplicates = async (temporaryUsers: DynamicUser[]): Promise<void> => {
  try {
    const database = await initDB()

    console.log(`🧹 Eliminando ${temporaryUsers.length} registros temporales duplicados`)

    for (const tempUser of temporaryUsers) {
      await database.delete("dynamicUsers", tempUser.id)
      console.log(`🗑️ Registro temporal eliminado: ID ${tempUser.id}`)
    }

    console.log(`✅ Limpieza de registros temporales completada`)
  } catch (error) {
    console.error("❌ Error limpiando registros temporales:", error)
    throw error
  }
}

// Helper function to validate dynamic user data
export const validateDynamicUser = (user: DynamicUser, schema: DynamicSchema): { valid: boolean; errors: string[] } => {
  const errors: string[] = []

  // Check if user has ID
  if (!user.id) {
    errors.push("Usuario debe tener un ID válido")
  }

  // Check required fields based on schema
  Object.entries(schema).forEach(([fieldName, fieldConfig]) => {
    if (fieldConfig.required) {
      const value = user[fieldName] !== undefined ? user[fieldName] : user.extra?.[fieldName]
      if (value === null || value === undefined || value === '') {
        errors.push(`Campo requerido: ${fieldConfig.label}`)
      }
    }
  })

  return {
    valid: errors.length === 0,
    errors
  }
}
